import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { ArrowLeftRegular, DeleteRegular } from '@fluentui/react-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { DisclaimerEditorForm } from '../components/CustomDisclaimers/DisclaimerEditorForm';
import { ConfirmationDialog } from '../components/Common/Dialogs';
import { SettingsBanner } from '../components/Common/SettingsBanner';
import { useSavingGuard } from '../hooks/useSavingGuard';
import useEventTracker from '../hooks/useEventTracker';
import { customDisclaimerService } from '../services/customDisclaimerService';
import { CustomDisclaimerServiceError } from '../services/customDisclaimerServiceError';
import type { CustomDisclaimer, DisclaimerDraft, DisclaimerFieldErrors } from '../types/customDisclaimers';
import { ElementButton, PageName } from '../tracking/tracking.types';
import {
  createEmptyDisclaimerDraft,
  createDisclaimerDeletedNotice,
  createDisclaimerUpdatedNotice,
  disclaimerDraftsMatch,
  formatDisclaimerUpdatedAt,
  hasDisclaimerFieldErrors,
  normalizeDisclaimerDraft,
  toDisclaimerDraft,
  validateDisclaimerDraft,
} from '../utils/customDisclaimerUtils';
import { useStyles } from './DisclaimerFormPage.style';

export const EditDisclaimer: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const eventTracker = useEventTracker();
  const eventTrackerRef = useRef(eventTracker);
  const [loadedDisclaimer, setLoadedDisclaimer] = useState<CustomDisclaimer | null>(null);
  const [draft, setDraft] = useState<DisclaimerDraft>(createEmptyDisclaimerDraft);
  const [errors, setErrors] = useState<DisclaimerFieldErrors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const mutationInProgress = saving || deleting;
  const { requestNavigation } = useSavingGuard(mutationInProgress);
  const isValid = !hasDisclaimerFieldErrors(validateDisclaimerDraft(draft));
  const isDirty = loadedDisclaimer ? !disclaimerDraftsMatch(draft, toDisclaimerDraft(loadedDisclaimer)) : false;

  const loadDisclaimer = useCallback(async () => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);
      setNotFound(false);
      const disclaimer = await customDisclaimerService.getById(id);

      if (!disclaimer) {
        setLoadedDisclaimer(null);
        setNotFound(true);
        return;
      }

      setLoadedDisclaimer(disclaimer);
      setDraft(toDisclaimerDraft(disclaimer));
      setErrors({});
    } catch {
      setLoadError('There was a problem loading this disclaimer. Try again later.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDisclaimer();
  }, [loadDisclaimer]);

  useEffect(() => {
    eventTrackerRef.current.eventTrackOnAppLaunched({ pageName: PageName.EDIT_DISCLAIMER }).catch(() => undefined);
  }, []);

  const trackElementClick = (elementName: ElementButton) => {
    eventTrackerRef.current.eventTrackOnElementClicked({
      elementType: 'button',
      elementName,
      pageName: PageName.EDIT_DISCLAIMER,
    }).catch(() => undefined);
  };

  const handleDraftChange = (nextDraft: DisclaimerDraft) => {
    const nextValidation = validateDisclaimerDraft(nextDraft);
    setDraft(nextDraft);
    setPageError(null);
    setErrors((currentErrors) => ({
      name: currentErrors.name ? nextValidation.name : undefined,
      organisationName: currentErrors.organisationName ? nextValidation.organisationName : undefined,
      text: currentErrors.text ? nextValidation.text : undefined,
    }));
  };

  const handleReturnToOverview = () => {
    trackElementClick(ElementButton.CANCEL_DISCLAIMER);
    requestNavigation(() => navigate('/custom-disclaimers'));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id || !loadedDisclaimer || saving || !isDirty) {
      return;
    }

    const validationErrors = validateDisclaimerDraft(draft);
    if (hasDisclaimerFieldErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    setErrors({});
    setPageError(null);

    try {
      const updatedDisclaimer = await customDisclaimerService.update(id, normalizeDisclaimerDraft(draft));
      trackElementClick(ElementButton.SAVE_DISCLAIMER);
      navigate('/custom-disclaimers', {
        state: {
          disclaimerNotice: createDisclaimerUpdatedNotice(updatedDisclaimer.name),
        },
      });
    } catch (error) {
      const errorCode = error instanceof CustomDisclaimerServiceError
        ? error.code
        : (error as { code?: string })?.code;
      if (errorCode === 'duplicate-name') {
        setErrors({ name: 'A disclaimer with this name already exists.' });
        setPageError('A disclaimer with this name already exists.');
      } else {
        setPageError('There was a problem saving the disclaimer. Try again later.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!id || !loadedDisclaimer || deleting) {
      return false;
    }

    setDeleting(true);
    setPageError(null);
    try {
      await customDisclaimerService.delete(id);
      trackElementClick(ElementButton.DELETE_DISCLAIMER);
      navigate('/custom-disclaimers', {
        state: {
          disclaimerNotice: createDisclaimerDeletedNotice(loadedDisclaimer.name),
        },
      });
      return true;
    } catch {
      setPageError('There was a problem deleting the disclaimer. Try again later.');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container} data-testid="edit-disclaimer-page">
        <div className={styles.content}>
          <div className={styles.stateCard}>
            <Spinner label="Loading disclaimer..." data-testid="edit-disclaimer-loading" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.container} data-testid="edit-disclaimer-load-error">
        <div className={styles.content}>
          <div className={styles.stateCard}>
            <Text className={styles.stateText}>{loadError}</Text>
            <Button appearance="primary" onClick={loadDisclaimer}>Try again</Button>
            <Button appearance="secondary" onClick={handleReturnToOverview}>Back to disclaimers</Button>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !loadedDisclaimer) {
    return (
      <div className={styles.container} data-testid="edit-disclaimer-not-found">
        <div className={styles.content}>
          <div className={styles.stateCard}>
            <Text className={styles.cardHeading}>Disclaimer not found</Text>
            <Text className={styles.stateText}>This disclaimer may have been deleted or is no longer available.</Text>
            <Button appearance="primary" onClick={handleReturnToOverview}>Back to disclaimers</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="edit-disclaimer-page">
      {pageError && (
        <div className={styles.pageMessage}>
          <SettingsBanner
            intent="error"
            message={pageError}
            onDismiss={() => setPageError(null)}
            barTestId="edit-disclaimer-error-banner"
            dismissTestId="edit-disclaimer-error-dismiss"
          />
        </div>
      )}

      <div className={styles.header}>
        <Button
          className={styles.backButton}
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={handleReturnToOverview}
          disabled={mutationInProgress}
          data-testid="edit-disclaimer-back-button"
        >
          Disclaimers
        </Button>
        <Text className={styles.title} data-testid="edit-disclaimer-title">Edit disclaimer</Text>
        <div className={styles.headerActions}>
          <Button
            className={styles.deleteButton}
            appearance="subtle"
            icon={<DeleteRegular />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={mutationInProgress}
            data-testid="edit-disclaimer-delete-button"
          >
            Delete disclaimer
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.inner}>
          <form className={styles.formCard} onSubmit={handleSave} noValidate>
            <Text className={styles.cardHeading}>Disclaimer details</Text>
            <Text className={styles.cardDescription}>
              Configure a disclaimer for users assigned to the policies you select. Changes can take up to 2 hours to take effect.
            </Text>
            <Text className={styles.updatedAt}>Last updated {formatDisclaimerUpdatedAt(loadedDisclaimer.updatedAt, true)}</Text>
            <DisclaimerEditorForm
              draft={draft}
              onChange={handleDraftChange}
              errors={errors}
              currentDisclaimerId={loadedDisclaimer.id}
              disabled={mutationInProgress}
            />
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                type="button"
                onClick={handleReturnToOverview}
                disabled={mutationInProgress}
                data-testid="edit-disclaimer-cancel-button"
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                type="submit"
                disabled={!isValid || !isDirty || mutationInProgress}
                data-testid="edit-disclaimer-save-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={`Delete (${loadedDisclaimer.name})`}
        message="This disclaimer will stop appearing for users in the policies it is assigned to. Do you want to continue?"
        confirmText="Delete"
        cancelText="Cancel"
        danger
        loading={deleting}
      />
    </div>
  );
};

export default EditDisclaimer;